import { useCallback, useMemo, useRef, useState } from 'react';

import { createEntity, mapApiCreatedEntityToUi } from '../../../services/entityCreate.js';
import { mapApiExistsToUi } from '../../../services/entityExists.js';
import {
  CREATABLE_TYPES,
  CREATE_STEP_IDS,
  IDENTITY_KEY_FIELD,
  isCreatableType,
} from '../constants/createRecordFields.js';
import {
  buildCreatePayload,
  detailsErrors,
  emptyValues,
  identityErrors,
  newPhoneRow,
} from '../utils/createRecordValidation.js';
import { apiErrorStatus } from './apiErrorStatus.js';
import useRecordExists from './useRecordExists.js';

const IDENTITY_STEP = 0;
const DETAILS_STEP = 1;
const REVIEW_STEP = 2;

const NO_OWNER = Object.freeze({ id: '', name: '' });

/** Which part of the owner each form field carries. */
const OWNER_PART = { ownerId: 'id', ownerName: 'name' };

const FAILURE_BY_STATUS = { 403: 'forbidden', 409: 'conflict', 422: 'invalid' };

/**
 * State and rules of the create-record wizard: Identity → Details → Review.
 *
 * One hook owns the lot because the steps are one transaction — nothing is sent
 * until Review, and going back never loses what was typed. Screens render; this
 * decides what may happen next.
 */
export function useCreateRecordWizard({ initialType, currentUser } = {}) {
  const [entityType, setEntityType] = useState(isCreatableType(initialType) ? initialType : CREATABLE_TYPES[0]);
  const [typed, setTyped] = useState(() => emptyValues(entityType));
  // Only what the user CHOSE. The default owner is the signed-in person and is
  // read from `currentUser` every render rather than copied once at mount: that
  // person may arrive after the first render, and a copy would leave the owner
  // empty or stale and make an untouched form look edited.
  const [ownerOverride, setOwnerOverride] = useState(null);
  const [stepIndex, setStepIndex] = useState(IDENTITY_STEP);
  const [touched, setTouched] = useState({});
  const [attempted, setAttempted] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [conflictMatches, setConflictMatches] = useState([]);
  const submittingRef = useRef(false);

  const owner = ownerOverride ?? currentUser ?? NO_OWNER;
  const values = useMemo(
    () => ({ ...typed, ownerId: owner.id ?? '', ownerName: owner.name ?? '' }),
    [typed, owner.id, owner.name],
  );

  const pristine = useMemo(() => emptyValues(entityType), [entityType]);
  const ownerChanged = ownerOverride !== null && ownerOverride.id !== (currentUser?.id ?? '');
  const isDirty = useMemo(() => {
    const strip = (v) => ({ ...v, phones: v.phones?.map(({ number, isPrimary }) => ({ number, isPrimary })) });
    return ownerChanged || JSON.stringify(strip(typed)) !== JSON.stringify(strip(pristine));
  }, [typed, pristine, ownerChanged]);

  const keyField = IDENTITY_KEY_FIELD[entityType];
  const identity = useMemo(() => identityErrors(entityType, values), [entityType, values]);
  const details = useMemo(() => detailsErrors(entityType, values), [entityType, values]);

  // The check only runs once the identifying field is itself valid: asking
  // whether "sa@" exists would flash a verdict on every keystroke.
  const exists = useRecordExists({
    entityType,
    key: { [keyField]: values[keyField] },
    enabled: stepIndex === IDENTITY_STEP && !identity[keyField],
  });

  const stepErrors = useMemo(
    () => (stepIndex === IDENTITY_STEP ? identity : stepIndex === DETAILS_STEP ? details : {}),
    [stepIndex, identity, details],
  );
  const errors = useMemo(() => {
    if (attempted[stepIndex]) return stepErrors;
    return Object.fromEntries(Object.entries(stepErrors).filter(([field]) => touched[field]));
  }, [attempted, stepIndex, stepErrors, touched]);

  const stepComplete = Object.keys(stepErrors).length === 0;
  // Why Next is unavailable even though the user did nothing wrong. An
  // incomplete step is NOT a block: Next stays clickable so that pressing it
  // can show what is missing.
  let blockReason = null;
  if (stepIndex === IDENTITY_STEP) {
    if (exists.exists) blockReason = 'exists';
    else if (exists.checking) blockReason = 'checking';
  }
  const canAdvance = stepIndex < REVIEW_STEP && stepComplete && !blockReason;

  const steps = useMemo(
    () =>
      CREATE_STEP_IDS.map((id, index) => ({
        id,
        state: index < stepIndex ? 'completed' : index === stepIndex ? 'current' : 'upcoming',
      })),
    [stepIndex],
  );

  const setField = useCallback(
    (name, value) => {
      if (OWNER_PART[name]) {
        setOwnerOverride((prev) => ({
          ...(prev ?? { id: currentUser?.id ?? '', name: currentUser?.name ?? '' }),
          [OWNER_PART[name]]: value,
        }));
      } else {
        setTyped((prev) => ({ ...prev, [name]: value }));
      }
      // A 409 answers for the value that was sent. Once the identifying field
      // changes, the matches it returned describe a record that is no longer the
      // one being typed, so they must not keep telling the user otherwise.
      if (name === keyField) setConflictMatches((prev) => (prev.length > 0 ? [] : prev));
    },
    [keyField, currentUser],
  );

  const touch = useCallback((name) => setTouched((prev) => ({ ...prev, [name]: true })), []);

  const selectType = useCallback(
    (nextType) => {
      if (!isCreatableType(nextType) || nextType === entityType) return;
      setEntityType(nextType);
      setTyped(emptyValues(nextType));
      setOwnerOverride(null);
      setStepIndex(IDENTITY_STEP);
      setTouched({});
      setAttempted({});
      setConflictMatches([]);
    },
    [entityType],
  );

  const goNext = useCallback(() => {
    if (!canAdvance) {
      setAttempted((prev) => ({ ...prev, [stepIndex]: true }));
      return false;
    }
    setStepIndex((prev) => prev + 1);
    return true;
  }, [canAdvance, stepIndex]);

  const goBack = useCallback(() => setStepIndex((prev) => Math.max(IDENTITY_STEP, prev - 1)), []);

  const goToStep = useCallback(
    (id) => {
      const target = CREATE_STEP_IDS.indexOf(id);
      if (target >= 0 && target < stepIndex) setStepIndex(target);
    },
    [stepIndex],
  );

  const addPhone = useCallback(
    () => setTyped((prev) => ({ ...prev, phones: [...prev.phones, newPhoneRow(false)] })),
    [],
  );

  const setPhoneNumber = useCallback(
    (id, number) =>
      setTyped((prev) => ({
        ...prev,
        phones: prev.phones.map((phone) => (phone.id === id ? { ...phone, number } : phone)),
      })),
    [],
  );

  const setPrimaryPhone = useCallback(
    (id) =>
      setTyped((prev) => ({
        ...prev,
        phones: prev.phones.map((phone) => ({ ...phone, isPrimary: phone.id === id })),
      })),
    [],
  );

  const removePhone = useCallback(
    (id) =>
      setTyped((prev) => {
        const remaining = prev.phones.filter((phone) => phone.id !== id);
        if (remaining.length === 0) return { ...prev, phones: [newPhoneRow(true)] };
        // The primary flag must survive the removal of the row that held it.
        return remaining.some((phone) => phone.isPrimary)
          ? { ...prev, phones: remaining }
          : { ...prev, phones: remaining.map((phone, index) => ({ ...phone, isPrimary: index === 0 })) };
      }),
    [],
  );

  const submit = useCallback(
    async ({ createAnother = false } = {}) => {
      // A ref, not state: two clicks in one tick both see `submitting === false`.
      if (submittingRef.current) return { ok: false, reason: 'busy' };
      submittingRef.current = true;
      setSubmitting(true);
      setConflictMatches([]);

      try {
        const { body } = await createEntity({ entityType, values: buildCreatePayload(entityType, values) });
        const created = mapApiCreatedEntityToUi(body);
        if (createAnother) {
          setTyped(emptyValues(entityType));
          setOwnerOverride(null);
          setStepIndex(IDENTITY_STEP);
          setTouched({});
          setAttempted({});
        }
        return { ok: true, created };
      } catch (error) {
        const reason = FAILURE_BY_STATUS[apiErrorStatus(error)] ?? 'error';
        if (reason === 'conflict') {
          setConflictMatches(mapApiExistsToUi(error.response?.data?.body).matches);
          setStepIndex(IDENTITY_STEP);
        }
        return { ok: false, reason };
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [entityType, values],
  );

  return useMemo(
    () => ({
      entityType,
      values,
      stepIndex,
      steps,
      errors,
      canAdvance,
      blockReason,
      exists,
      conflictMatches,
      isDirty,
      submitting,
      selectType,
      setField,
      touch,
      goNext,
      goBack,
      goToStep,
      addPhone,
      setPhoneNumber,
      setPrimaryPhone,
      removePhone,
      submit,
    }),
    [
      entityType, values, stepIndex, steps, errors, canAdvance, blockReason, exists, conflictMatches, isDirty, submitting,
      selectType, setField, touch, goNext, goBack, goToStep, addPhone, setPhoneNumber, setPrimaryPhone,
      removePhone, submit,
    ],
  );
}

export default useCreateRecordWizard;
